import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useSession } from '../../lib/auth.js';
import ApiKeyModal from '../../components/ApiKeyModal.jsx';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export default function ApiKeys() {
  const { workspaces } = useSession();
  const ws = workspaces && workspaces[0];
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newKey, setNewKey] = useState(null);

  const load = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/v1/keys?workspace_id=${ws.id}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setKeys(data.keys || []);
      }
    } catch (_) { /* swallow */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [ws?.id]);

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      const name = `key-${new Date().toISOString().slice(0, 10)}-${Math.random().toString(36).slice(2, 6)}`;
      const r = await fetch(`${API_BASE}/v1/keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: ws.id, name }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data?.error?.message || `HTTP ${r.status}`);
        return;
      }
      setNewKey({ name: data.name, plaintext: data.plaintext });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this key? API calls using it will fail immediately.')) return;
    try {
      await fetch(`${API_BASE}/v1/keys/${id}?workspace_id=${ws.id}`, { method: 'DELETE', credentials: 'include' });
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      <div style={headerRow}>
        <h1 style={title}>API Keys</h1>
        <button onClick={generate} disabled={busy} style={primaryBtn}>
          {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
          Generate Key
        </button>
      </div>

      {error && <p style={errText}>{error}</p>}

      {loading ? (
        <div style={center}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : keys.length === 0 ? (
        <p style={muted}>No API keys yet. Generate one to start making requests.</p>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Prefix</th>
              <th style={th}>Created</th>
              <th style={th}>Last used</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td style={td}>{k.name}</td>
                <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-12)' }}>{k.prefix}****</td>
                <td style={td}>{new Date(k.created_at).toLocaleDateString()}</td>
                <td style={td}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}</td>
                <td style={td}>
                  <button onClick={() => revoke(k.id)} style={delBtn}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {newKey && (
        <ApiKeyModal
          name={newKey.name}
          plaintext={newKey.plaintext}
          onClose={() => setNewKey(null)}
        />
      )}
    </div>
  );
}

const headerRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-5)' };
const title = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)', margin: 0 };
const center = { textAlign: 'center', padding: 'var(--space-8)', color: 'var(--fg-2)' };
const muted = { color: 'var(--fg-3)', fontSize: 'var(--fs-14)' };
const errText = { color: 'var(--danger)', fontSize: 'var(--fs-13)', marginBottom: 'var(--space-3)' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-4)', background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-13)', fontWeight: 500, cursor: 'pointer' };
const table = { width: '100%', borderCollapse: 'collapse' };
const th = { textAlign: 'left', fontSize: 'var(--fs-11)', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--line-1)' };
const td = { padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--fs-13)', color: 'var(--fg-1)', borderBottom: '1px solid var(--line-1)' };
const delBtn = { background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 4 };
